import { useMemo, useState } from "react";
import {
  Stack,
  Text,
  PasswordInput,
  Button,
  Alert,
  List,
  ThemeIcon,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconX } from "@tabler/icons-react";
import { PASSWORD_POLICY } from "@livingsyncs/types";

interface NewPasswordRequiredProps {
  /** Email the user signed in with — shown read-only as a reassurance. */
  email: string | null;
  onSubmit: (newPassword: string) => void;
  isPending: boolean;
  error: Error | null;
}

// Mirror of the Cognito pool password policy (via the shared PASSWORD_POLICY)
// so the form can disable submit until the password is valid — Cognito rejects
// weak passwords server-side anyway, this just makes the failure mode obvious.
const RULES: { id: string; label: string; test: (p: string) => boolean }[] = [
  {
    id: "len",
    label: `At least ${PASSWORD_POLICY.minLength} characters`,
    test: (p) => p.length >= PASSWORD_POLICY.minLength,
  },
  { id: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { id: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "digit", label: "A number", test: (p) => /\d/.test(p) },
  {
    id: "symbol",
    label: "A symbol (e.g. ! @ # $ %)",
    // Cognito's default symbol class is ^$*.[]{}()?-"!@#%&/\,><':;|_~`+=
    test: (p) => /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(p),
  },
];

export function NewPasswordRequired({
  email,
  onSubmit,
  isPending,
  error,
}: NewPasswordRequiredProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const ruleResults = useMemo(
    () => RULES.map((r) => ({ ...r, passed: r.test(newPassword) })),
    [newPassword],
  );
  const allRulesPass = ruleResults.every((r) => r.passed);
  const matches = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = allRulesPass && matches;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) onSubmit(newPassword);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Text size="sm" fw={500}>
          Set a new password
        </Text>
        <Text size="sm" c="dimmed">
          {email ? (
            <>
              Welcome,{" "}
              <Text component="span" fw={500}>
                {email}
              </Text>
              . Your account uses a temporary password — please choose a
              permanent one to continue.
            </>
          ) : (
            "Your account uses a temporary password — please choose a permanent one to continue."
          )}
        </Text>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            title="Couldn't set password"
            role="alert"
          >
            {error.message ||
              "Cognito rejected the new password. Please try again."}
          </Alert>
        )}

        <PasswordInput
          label="New password"
          placeholder="Choose a new password"
          required
          autoFocus
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          aria-label="New password"
        />

        <PasswordInput
          label="Confirm new password"
          placeholder="Re-enter the password"
          required
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={
            confirmPassword.length > 0 && !matches
              ? "Passwords don't match"
              : null
          }
          aria-label="Confirm new password"
        />

        <List
          spacing={4}
          size="xs"
          c="dimmed"
          icon={
            <ThemeIcon color="gray" size={14} radius="xl">
              <IconX size={10} />
            </ThemeIcon>
          }
        >
          {ruleResults.map((r) => (
            <List.Item
              key={r.id}
              icon={
                r.passed ? (
                  <ThemeIcon color="green" size={14} radius="xl">
                    <IconCheck size={10} />
                  </ThemeIcon>
                ) : undefined
              }
            >
              {r.label}
            </List.Item>
          ))}
        </List>

        <Button
          type="submit"
          fullWidth
          size="md"
          loading={isPending}
          disabled={!canSubmit}
          aria-label="Set new password and continue"
        >
          Set password and continue
        </Button>
      </Stack>
    </form>
  );
}
